import { Component, OnInit, EventEmitter, Output } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';

@Component({
  selector: 'app-dashboard-fund-approval-drafts',
  templateUrl: './dashboard-fund-approval-drafts.component.html',
  styleUrls: ['./dashboard-fund-approval-drafts.component.scss'],
})
export class DashboardFundApprovalDraftsComponent implements OnInit {
  @Output() onClick = new EventEmitter<MouseEvent>();

  subTabs: Tab[] = [
    { label: 'Minutas', value: 'drafts' },
    { label: 'Templates vinculados', value: 'templates' },
  ];

  subTabIndex = 0;
  activeSubTab = 'drafts';

  linkForm = this.formBuilder.group({
    draft: [null, []],
  });

  filterForm = this.formBuilder.group({
    name: [null, []],
    risk: [null, []],
    status: [null, []],
  });

  riskItems = [
    { label: 'Com risco', value: 'with_risk' },
    { label: 'Sem risco', value: 'no_risk' },
  ];

  statusFilterItems = [
    { label: 'Ativado', value: 'ativo' },
    { label: 'Desativado', value: 'inativo' },
    { label: 'Rascunho', value: 'rascunho' },
  ];

  showFilterModal = false;
  hasFilters = false;
  count = 0;
  offset = 0;
  resultsPerPage = 10;

  columns: TableColumnList<AttachmentListValues>[] = [
    {
      name: 'Nome',
      cell: (row) => row.file,
      size: 3,
    },
    {
      name: 'Destino',
      cell: (row) => this.formatApplicable(row.applicable),
    },
    {
      name: 'Última atualização',
      cell: (row) => this.formatDate(row.date_updated),
    },
    {
      name: '',
      cell: (row) =>
        `<div class="d-flex align-items-center icon-container">
              <div class="attachment-reject-button row-${row.id}">
                <svg-icon src="assets/images/icon-trash.svg"></svg-icon>
              </div>
              <div class="document-download row-${row.id}">
                  <svg-icon src="assets/images/icon-download.svg"></svg-icon>
              </div>
              <div class="document-visualizer row-${row.id}">
                  <svg-icon src="assets/images/icon-eye.svg"></svg-icon>
              </div>
          </div>`,
    },
  ];
  data: FundDraftRow[] = [];

  showDocument = false;

  document: any;

  fundId: number = 0;
  applicableRole = 'ADM';

  linkModal = false;
  linkModalMode: 'draft' | 'template' = 'draft';
  showUnlinkModal = false;
  selectedToUnlink: FundDraftRow | null = null;
  showCancelModal = false;
  selectedToCancel: FundDraftRow | null = null;

  drafts = [];
  draftsItems = [];

  canView = false;
  canAdd = false;

  loading = true;

  constructor(
    private formBuilder: FormBuilder,
    private routeParams: ActivatedRoute,
    private authService: AuthService,
    private route: Router,
    private api: ApiService,
    private toast: ToastService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    if (this.authService.user.active_register) {
      const role =
        this.authService.user.active_register.register.role.slug.split('-');
      this.applicableRole = role[0];
    }

    this.fundId = this.routeParams.snapshot.params.id;

    this.canView = this.authService.verifyPermission(
      'de_can_view_fund_draft_details',
    );

    this.canAdd = this.authService.verifyPermission(
      'de_can_view_fund_draft_add',
    );

    this.fetchData();
  }

  async fetchData() {
    this.loading = true;
    await Promise.all([this.fetchFundDrafts(), this.fetchDrafts()]);
    this.loading = false;
  }

  onChangeSubTab(index: number) {
    this.subTabIndex = index;
    this.activeSubTab = this.subTabs[index]?.value ?? 'drafts';
  }

  getDraftStatus(item: Pick<DraftDocument, 'error' | 'is_draft' | 'enabled'>) {
    if (item.error) {
      return { label: 'Erro', cssClass: '-inactive' };
    }

    if (item.is_draft) {
      return { label: 'Rascunho', cssClass: 'draft' };
    }

    if (item.enabled) {
      return { label: 'Ativado', cssClass: '' };
    }

    return { label: 'Desativado', cssClass: '-inactive' };
  }

  formatApplicable(applicable: string) {
    switch (applicable) {
      case 'assignor':
        return 'Cedente';
      case 'shareholder':
        return 'Cotista';
      case 'provider':
        return 'Prestador de serviço';
      default:
        return 'Cedente';
    }
  }

  extractDraftList(res: any): any[] {
    if (Array.isArray(res?.results)) {
      return res.results;
    }

    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res)) {
      return res;
    }

    return [];
  }

  isDraftLinkable(item: Pick<DraftDocument, 'error' | 'is_draft' | 'enabled'>) {
    return !item.error && !item.is_draft && !!item.enabled;
  }

  async fetchDrafts() {
    try {
      const res = await this.api.get({
        route: 'api/draft/',
        token: true,
        params: {
          no_pagination: true,
          status: 'ativo',
        },
      });

      const list = this.extractDraftList(res).filter((item) =>
        this.isDraftLinkable(item),
      );

      this.drafts = list;
      this.draftsItems = list.map((item: any) => ({
        label: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error(error);
    }
  }

  async fetchFundDrafts(page = 1) {
    try {
      const res = await this.api.get({
        route: 'api/draft/',
        token: true,
        params: {
          page,
          in__fund: this.fundId,
          ...this.buildDraftFilters(),
        },
      });

      const drafts = this.extractDraftList(res);

      this.count = res?.count ?? drafts.length;
      this.offset = res?.offset ?? 0;
      this.resultsPerPage = res?.results_per_page ?? this.resultsPerPage;

      this.data = drafts.map((item: DraftDocument & { risk?: string }) => {
        const status = this.getDraftStatus(item);

        return {
          id: item.id,
          name: item.name,
          documentType: item.type?.name || '-',
          date_updated: item.updated_at,
          riskLabel: this.formatRisk(item.risk),
          statusLabel: status.label,
          statusClass: status.cssClass,
        };
      });
    } catch (error) {
      console.error(error);
    }
  }

  buildDraftFilters() {
    const filters: Record<string, string> = {};

    Object.entries(this.filterForm.value).forEach(([key, value]) => {
      if (value !== null && value !== '' && value !== undefined) {
        filters[key] = String(value);
      }
    });

    return filters;
  }

  formatRisk(risk?: string | null) {
    if (risk === 'with_risk') return 'Com risco';
    if (risk === 'no_risk') return 'Sem risco';
    return '-';
  }

  async changePage(page: number) {
    this.loading = true;
    await this.fetchFundDrafts(page);
    this.loading = false;
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;
  }

  async applyFilters() {
    this.hasFilters = Object.keys(this.buildDraftFilters()).length > 0;
    this.showFilterModal = false;
    this.loading = true;
    await this.fetchFundDrafts(1);
    this.loading = false;
  }

  async resetFilters() {
    this.filterForm.reset();
    this.hasFilters = false;
    this.showFilterModal = false;
    this.loading = true;
    await this.fetchFundDrafts(1);
    this.loading = false;
  }

  findItem(id: any) {
    const finded = this.data.find((item: any) => {
      if (item.id) {
        return Number(item.id) === Number(id);
      }

      return false;
    });

    if (finded) {
      this.document = finded;
    }

    return finded;
  }
  handleEdit(id: any) {
    if (this.findItem(id)) {
      this.route.navigateByUrl(`/app/drafts-old/${id}/${this.fundId}`);
    }
  }

  async handleDelete(id: any) {
    if (this.findItem(id)) {
      try {
        const res = await this.api.delete({
          route: `api/draft/${this.document.id}/`,
          token: true,
        });

        this.data = this.data.filter(
          (item: any) => Number(item.id) !== Number(this.document.id),
        );

        this.document = null;
      } catch (error) {
        console.error(error);
      }
    }
  }

  async handleOpenVisualizer(id: any) {
    if (!this.findItem(id)) {
      return;
    }

    try {
      const token = this.authService.getToken();
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
      });
      const response = await this.http
        .get(`${this.api.url}/api/draft/${this.document.id}/preview/`, {
          headers,
          responseType: 'blob',
        })
        .toPromise();

      if (!response) {
        return;
      }

      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank')?.focus();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Não foi possível visualizar o template',
      );
    }
  }
  addNewDocument() {
    this.route.navigateByUrl(`/app/drafts-old/new/${this.fundId}`);
  }

  formatDate(date: string, haveHours = true) {
    if (date === null) return '';
    const day = date.split('T').shift()?.split('-').reverse().join('/');
    const hours = date.split('T').pop()?.slice(0, 5);

    return `${day} ${haveHours ? 'às ' + hours : ''}`;
  }

  closeModal() {
    this.showDocument = false;
    this.document = null;
  }

  openLinkModal(mode: 'draft' | 'template') {
    this.linkModalMode = mode;
    this.linkForm.reset();
    this.linkModal = true;
  }

  toggleLinkModal(status: boolean) {
    this.linkModal = status;

    if (!status) {
      this.linkForm.reset();
    }
  }

  get linkModalTitle() {
    return this.linkModalMode === 'template'
      ? 'Vincular template'
      : 'Vincular minuta';
  }

  get linkModalLabel() {
    return this.linkModalMode === 'template'
      ? 'Selecione o template'
      : 'Seleciona as minutas';
  }

  get linkModalAction() {
    return this.linkModalMode === 'template'
      ? 'Vincular template'
      : 'Vincular minuta';
  }

  async submitForm() {
    const { draft } = this.linkForm.value;

    if (!draft) {
      return;
    }

    try {
      await this.api.post({
        route: `api/draft/${draft}/link/`,
        body: { fund: this.fundId },
        token: true,
      });

      this.toggleLinkModal(false);
      this.toast.show('info', 'Sucesso', 'Template vinculado ao fundo');
      this.fetchData();
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Não foi possível vincular o template');
    }
  }

  async sendToSignature(item: FundDraftRow) {
    try {
      await this.api.post({
        route: `api/draft/${item.id}/send/`,
        body: { fund: this.fundId },
        token: true,
      });

      this.toast.show('info', 'Sucesso', 'Minuta enviada para assinatura');
      this.fetchData();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Não foi possível enviar a minuta para assinatura',
      );
    }
  }

  openCancelModal(item: FundDraftRow) {
    this.selectedToCancel = item;
    this.showCancelModal = true;
  }

  closeCancelModal() {
    this.showCancelModal = false;
    this.selectedToCancel = null;
  }

  get cancelMessage() {
    const name = this.selectedToCancel?.name || 'esta minuta';
    return `Tem certeza que deseja cancelar ${name}?`;
  }

  async confirmCancel() {
    if (!this.selectedToCancel) {
      return;
    }

    const item = this.selectedToCancel;
    this.showCancelModal = false;

    try {
      await this.api.post({
        route: `api/draft/${item.id}/cancel/`,
        body: { fund: this.fundId },
        token: true,
      });

      this.toast.show('info', 'Sucesso', 'Minuta cancelada');
      this.selectedToCancel = null;
      this.fetchData();
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Não foi possível cancelar a minuta');
    }
  }

  openUnlinkModal(item: FundDraftRow) {
    this.selectedToUnlink = item;
    this.showUnlinkModal = true;
  }

  closeUnlinkModal() {
    this.showUnlinkModal = false;
    this.selectedToUnlink = null;
  }

  get unlinkMessage() {
    const name = this.selectedToUnlink?.name || 'este template';
    return `Tem certeza que deseja desvincular ${name} deste fundo?`;
  }

  async confirmUnlink() {
    if (!this.selectedToUnlink) {
      return;
    }

    const item = this.selectedToUnlink;
    this.showUnlinkModal = false;

    try {
      await this.api.delete({
        route: `api/draft/${item.id}/link/`,
        params: { fund: this.fundId },
        token: true,
      });

      this.toast.show('info', 'Sucesso', 'Template desvinculado');
      this.selectedToUnlink = null;
      this.fetchData();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Não foi possível desvincular o template',
      );
    }
  }
}

type FundDraftRow = {
  id: number;
  name: string;
  documentType: string;
  date_updated: string;
  riskLabel: string;
  statusLabel: string;
  statusClass: string;
};
